import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { creditCardImagesMapper } from '@app/shared/mappers/credit-card.mapper';
import { ICard, ICardFreezeUnfreezeReason, IContract, ISharedCards } from '@app/shared/models/contract.model';
import { IDynamicDialogConfig } from '@app/shared/modules/dynamic-dialog/model';
import { SubscriberComponent } from '@app/shared/modules/subscriber/base/subscriber.component.base';
import { AmplitudeService } from '@app/shared/services/amplitude.service';
import { ContractContext } from '@app/shared/services/context/contract.context.service';
import { CustomerContext } from '@app/shared/services/context/customer.context.service';
import { ModalService } from '@app/shared/services/modal.service';
import { CardBasicService } from '@app/shared/services/wallet/card-basic.service';
import { STATUS, CARD_STATE } from '@app/shared/types/enums';
import { ACTION_EVENT_PROPERTY, ACTION_NAME, AMPLITUDE_EVENT } from '@app/shared/types/enums/amplitude';
import { CONTRACT_TYPE, CARD_TYPE } from '@shared/types/enums';
import { AuthService } from '@auth0/auth0-angular';
import { EMPTY, filter, Observable, Subject, switchMap, catchError, throwError, tap, takeUntil } from 'rxjs';
import { IFreezeUnfreezeCardRequest } from '../../../../../models/banking.model';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '@app/shared/services/notification.service';
import { CARD_COMPONENT } from '@app/shared/modules/buttons-actions/components/card-actions-buttons/card-modal.enum';

@Component({
  selector: 'app-card-freeze',
  templateUrl: './card-freeze.component.html',
  styleUrls: ['./card-freeze.component.sass'],
})
export class CardFreezeComponent extends SubscriberComponent implements OnInit, OnDestroy {
  currentCard!: ICard | undefined;
  contract!: IContract | null;
  public sharedCards?: ISharedCards | null;
  public hasSharedCard = false;
  public isLoading = false;
  public freezeCardForm!: FormGroup;
  public reasons$: Observable<ICardFreezeUnfreezeReason[] | undefined> = EMPTY;
  initialState!: IDynamicDialogConfig;
  STATUS = STATUS;
  FROOZEN_STATE = CARD_STATE.FROZEN;
  imagesCard = creditCardImagesMapper;
  contractType = CONTRACT_TYPE.CREDIT;
  cardType = CARD_TYPE.VIRTUAL;
  alertMessage = '';

  constructor(
    private readonly _modalSvc: ModalService,
    public readonly _contractContextService: ContractContext,
    private readonly _cardBasicSvc: CardBasicService,
    private readonly _amplitudeSvc: AmplitudeService,
    private readonly _customerCtxSvc: CustomerContext,
    private readonly _authSvc: AuthService,
    private readonly _trsSvc: TranslateService,
    private readonly _notificationSvc: NotificationService
  ) {
    super();
  }

  ngOnInit() {
    this.logInitEvent();
    this.getModalData();
    this.createForm();
    this.getContractAndCard();
    this.getFreezeUnfreezeReasons();
    this.freezeUnfreezeSubscription();
  }

  private logInitEvent(): void {
    this._amplitudeSvc.logEvent<ACTION_EVENT_PROPERTY>(AMPLITUDE_EVENT.ACTION_INIT, {
      actionName: ACTION_NAME.FREEZE_CARD,
    });
  }

  getContractAndCard() {
    this.contract = this._contractContextService.getCurrentContract();
    this.sharedCards = this._contractContextService.getSharedCards();
    this.contractType = this.contract?.type === CONTRACT_TYPE.CREDIT ? CONTRACT_TYPE.CREDIT : CONTRACT_TYPE.DEBIT;

    let {
      modalComponentData: { cardCode },
    } = this.initialState;

    this.hasSharedCard = this.sharedCards?.additional_cards.length ? this.sharedCards?.additional_cards.length > 0 : false;
    this.currentCard = this.contract?.cards.find(card => card.code === cardCode);

    if (!this.currentCard?.code && this.hasSharedCard) {
      this.currentCard = this.sharedCards?.additional_cards.find(card => card.code === cardCode);
    }

    this.cardType = this.currentCard?.type === CARD_TYPE.VIRTUAL ? CARD_TYPE.VIRTUAL : CARD_TYPE.PHYSICAL;
    this.alertMessage = this.currentCard?.state === CARD_STATE.FROZEN ? 'MODAL_CARDS.UNFREEZE.ALERT' : 'MODAL_CARDS.FREEZE.ALERT';
  }

  private freezeUnfreezeSubscription(): void {
    this._modalSvc.isPrimaryActionClicked$
      .pipe(
        takeUntil(this.destroy$),
        filter(value => value),
        switchMap(() => this._authSvc.user$),
        switchMap(agentData => {
          const email_support = agentData?.email || '';
          const rppUserId = this._customerCtxSvc.getRppUserId();
          const body: IFreezeUnfreezeCardRequest = {
            state: this.currentCard?.state === CARD_STATE.FROZEN ? CARD_STATE.ACTIVE : CARD_STATE.FROZEN,
            reason: this.freezeCardForm.get('reason')?.value || '',
            email_support,
            sub_state: this.currentCard?.sub_state || '',
            notify_external: 'true',
          };

          return this._cardBasicSvc.freezeUnfreezeCard(rppUserId || '', this.currentCard?.code || '', body);
        }),
        tap(() => {
          this._modalSvc.closeWithSuccessMsg();
          let messageSuccess =
            this.currentCard?.state === CARD_STATE.FROZEN
              ? this._trsSvc.instant('MODAL_CARDS.SNACK_BAR.SUCCESS_UNFREEZE')
              : this._trsSvc.instant('MODAL_CARDS.SNACK_BAR.SUCCESS');
          this.notification(messageSuccess, 3000, 'green_check', ['snackbar-success'], true);
        }),
        catchError(e => {
          return throwError(() => {
            this._modalSvc.closeWithErrorMsg();
            this.notification(this._trsSvc.instant('MODAL_CARDS.SNACK_BAR.ERROR'), 3000, 'error', ['snackbar-failure'], false);
          });
        })
      )
      .subscribe();
  }

  getFreezeUnfreezeReasons() {
    this.contract = this._contractContextService.getCurrentContract();
    this.currentCard = this.contract?.cards.find(card => card.code === this.currentCard?.code);
    let toogleFreeze = this.currentCard?.state === CARD_STATE.FROZEN ? 'UNFREEZE' : 'FREEZE';
    this.reasons$ = this._cardBasicSvc.getFreezeUnfreezeReasons(toogleFreeze) ?? EMPTY;
  }

  createForm() {
    this.freezeCardForm = new FormGroup({
      reason: new FormControl('', Validators.required),
    });
    this.onChangesForm();
  }

  onChangesForm() {
    this._subscriptions.sink = this.freezeCardForm.valueChanges.subscribe(value => {
      if (this.freezeCardForm.valid) {
        const data = {
          ...this.initialState,
          modalComponentData: {
            ...this.initialState.modalComponentData,
            acceptButtonTitleDisabled: false,
            formData: value,
          },
        };

        this._modalSvc.setModalData(data);
      }
    });
  }

  getModalData() {
    this._subscriptions.sink = this._modalSvc.getModalData().subscribe((data: IDynamicDialogConfig) => (this.initialState = data));
  }

  notification(message: string, time: number, icon: string, theme: string[], isSVG = false) {
    this._notificationSvc.openSnackBar(message, time, undefined, undefined, icon, theme, isSVG);
  }

  ngOnDestroy() {
    this.unsubscribe();
  }
}
